import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError, apiFetch, resolveApiUrl, resolveTtyEndpoint } from "@/api/client";
import { PROJECTS_ROOT_QUERY_KEY } from "@/api/projects";

// Wire format mirrors src/flowstack/server/routes/workspace.py (Phase 2 C2.3,
// U2.1 fs#2336) and the tty broker in routes/workspace_tty.py (U2.2 fs#2337).
// Every route is keyed by the project SLUG (D10).

export type WorkspaceState =
  | "starting"
  | "running"
  | "idle_suspended"
  | "stopping"
  | "stopped"
  | "failed";

/** `GET /api/projects/{slug}/workspace` — the project's one session. */
export interface WorkspaceStatus {
  session_id: string;
  project_id: string;
  user_id: string;
  state: WorkspaceState;
  stop_reason: string | null;
  started_at: string;
  last_activity_at: string;
  ended_at: string | null;
  running_seconds: number;
  hours_captured: number;
  workspace_image_digest: string | null;
  error: string | null;
  last_sync_error: string | null;
  /** The USER connection token, only while `starting` or `running`; minted
   * afresh on every status read, which is how a reconnect refreshes it. */
  token: string | null;
  token_expires_at: string | null;
}

/** How often the panel re-reads a session that is still `starting`. */
const STARTING_POLL_MS = 3_000;

export function workspaceQueryKey(slug: string): readonly unknown[] {
  return [...PROJECTS_ROOT_QUERY_KEY, "workspace", slug] as const;
}

export function workspacePath(slug: string): string {
  return `/api/projects/${encodeURIComponent(slug)}/workspace`;
}

/** The session, or `null` when the project has none (404 `no_workspace_session`). */
export async function fetchWorkspace(slug: string): Promise<WorkspaceStatus | null> {
  try {
    return await apiFetch<WorkspaceStatus>(workspacePath(slug));
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  }
}

export function useWorkspace(slug: string) {
  return useQuery({
    queryKey: workspaceQueryKey(slug),
    queryFn: () => fetchWorkspace(slug),
    enabled: Boolean(slug),
    refetchInterval: (query) =>
      query.state.data?.state === "starting" || query.state.data?.state === "stopping"
        ? STARTING_POLL_MS
        : false,
  });
}

/** `POST …/workspace` — start, or resume an `idle_suspended` session. */
export function useStartWorkspace(slug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<WorkspaceStatus>(workspacePath(slug), { method: "POST" }),
    onSuccess: (status) => {
      queryClient.setQueryData(workspaceQueryKey(slug), status);
    },
  });
}

/**
 * The browser leg of the tty broker: `wss …/workspace/tty` on the terminal
 * host when portal-config.json names an allowed `ttyEndpoint` (C2.9 (1)),
 * else on the API's own host (the configured API endpoint, else this page's
 * origin). Built from the slug, never from response data.
 */
export async function workspaceTtyUrl(slug: string): Promise<string> {
  const ttyEndpoint = await resolveTtyEndpoint();
  if (ttyEndpoint !== null) {
    const url = new URL(`${workspacePath(slug)}/tty`, ttyEndpoint);
    url.protocol = "wss:";
    return url.toString();
  }
  const resolved = await resolveApiUrl(`${workspacePath(slug)}/tty`);
  const url = new URL(resolved, window.location.href);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return url.toString();
}
